import { useRouter } from "@src/lib/router";
import React from "react";
import styles from "../../styles/ContentList.module.scss";
import ContentShimmmer from "../ContentShimmer";
import ListItem from "./ListItem";
import NoContentScreen from "./NoContentScreen";
import LockedModal from "./LockedModal";
import {
  COURSE_TABS_ENUMS,
} from "@src/utils/constants";
import { useSelector, useDispatch } from "react-redux";
import { hitGetOfflineDownloadedItemList } from "../../helpers/handleDownloadContent";
import FeaturedVideo from "../ContentHome/FeaturedVideo";
import NotesAttachmentModal from "@src/components/BottomSheets/components/NotesAttachmentModal/NotesAttachmentModal";
import ListItemForAttachments from "./ListItemForAttachments";
import { safeJsonParse } from "@src/utils/safeJsonParse";
import 'intersection-observer';

const ContentList = ({
  contentHeading,
  featuredVideo,
  contentList,
  contentLoading,
  contentHasMore,
  handleLoadMoreContent,
  contentListLength,
  noContentText,
  subtext,
  noContentHeight,
  isTrialContent=false
}) => {
  // NEXTJS ROUTER
  const router = useRouter();

  // INTERSECTION OBSERVER TO LOAD MORE DATA ON CONTENT SCROLL REACHING THE END
  const observer = React.useRef();
  const lastRowRef = React.useCallback(
    (element) => {
      if (contentLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(([entries]) => {
        if (
          (entries.isIntersecting || entries.intersectionRatio) &&
          contentHasMore
        ) {
          handleLoadMoreContent && handleLoadMoreContent();
        }
      });
      if (element) observer.current.observe(element);
    },
    [contentLoading, contentHasMore, handleLoadMoreContent]
  );
  const dispatch = useDispatch();
  // FETCHING STATES FROM REDUCER
  const isContentDownloadable = useSelector(
    (state) =>
      state?.overviewReducer?.overViewDetails?.course?.metaData?.isDownloadable
  );
  const contentTabId = useSelector(
    (state) =>
      state?.tabBarReducer?.tabList?.find?.(
        (tab) => tab?.enumKey === COURSE_TABS_ENUMS.CONTENT
      )?.id
  );
  const liveClassTabId = useSelector(
    (state) =>
      state?.tabBarReducer?.tabList?.find?.(
        (tab) => tab?.enumKey === COURSE_TABS_ENUMS.LIVECLASS
      )?.id
  );

  // OFFLINE DOWNLOADED ITEMS LIST FROM NATIVE APP STORED IN A MAP
  // URL => detailsObject(id, url, state, progress)
  const [offlineDownloadedListMap, setOfflineDownloadedListMap] =
    React.useState(new Map());

  // Always key the offline-download map by `url` so iOS and Android use the
  // same identifier. Previously iOS keyed by `id`, which is not globally unique
  // across the app's content catalog and caused PDF attachments to falsely
  // match unrelated downloaded items, hiding their download icon.
  const getOfflineDownloadedItem = (item) =>
    offlineDownloadedListMap.get(item?.url);

  // FUNCTION TO GET OFFLINE DOWNLOADED ITEM PROGRESS
  const getOfflineDownloadedItemProgress = (item) =>
    getOfflineDownloadedItem(item)?.progress;

  // FUNCTION TO GET OFFLINE DOWNLOADED ITEM STATE
  const getOfflineDownloadedItemState = (item) =>
    getOfflineDownloadedItem(item)?.state;

  // COPY OF OFFLINE DOWNLOAD LIST STATE TO AVOID LOSS OF DATA DUE TO ASYNC BEHAVAHIOUR OF SET STATE
  const offlineDownloadedListMapRef = React.useRef(offlineDownloadedListMap);

  // FETCHING LIST OF CURRENT OFFLINE DOWNLOADED MATERIAL ON CONTENT/FOLDER MOUNT
  React.useEffect(() => {
    if (
      router?.query?.courseId &&
      (router?.query?.tabId == contentTabId ||
        router?.query?.tabId == liveClassTabId ||
        router?.query?.folderId)
    ) {
      // SETTING TIME INTERVAL TO FETCH OFFLINE DOWNLOAD ITEM LIST
      // LIST CONTAINS (already downloaded items, currently in progress, queued for download)
      // console.log("U______", isContentDownloadable);
      isContentDownloadable && handleSetCheckProgressInterval();
      // handleSetCheckProgressInterval();

      // CLEARING GET OFFLINE DOWNLOAD INTERVALS ON UNMOUNT
      return () => {
        handleClearCheckProgressInterval();
      };
    }
  }, [
    router.query.tabId,
    contentTabId,
    liveClassTabId,
    router?.query?.folderId,
    isContentDownloadable,
  ]);

  // SETTING TIME INTERVAL TO FETCH OFFLINE DOWNLOAD ITEM LIST
  // LIST CONTAINS (already downloaded items, currently in progress, queued for download)

  // CHECK OFFLINE DOWNLOAD PROGRESS TIMER REF
  const getOfflineDownloadTimerRef = React.useRef(null);

  // FUNCTION TO SET CHECK PROGRESS INTERVAL
  const handleSetCheckProgressInterval = () => {
    // alert();
    handleClearCheckProgressInterval();
    handleGetOfflineDownloadList();
    getOfflineDownloadTimerRef.current = setInterval(() => {
      handleGetOfflineDownloadList();
    }, 2000);
  };
  // FUNCTION TO CLEAR CHECK PROGRESS INTERVAL
  const handleClearCheckProgressInterval = () => {
    clearInterval(getOfflineDownloadTimerRef.current);
  };

  // FUNCION TO FETCH CURRENTLY OFFLINE DOWNLOADED CONTENT FROM APP
  const handleGetOfflineDownloadList = () => {
    // PERFORMING DEEPLINK ACTION TO GET OFFLINE DOWNLOAD
    hitGetOfflineDownloadedItemList(router?.query?.courseId);

    // ADDING FUNCTION TO WINDOW TO HANDLE OFFLINE LIST
    window.onOfflineDownloadListUpdate = (offlineDownloadedList) => {
      let hasProgress = false;
      const parsedJSON = safeJsonParse(offlineDownloadedList, {
        source: "ContentList [onOfflineDownloadListUpdate]",
        fallback: {},
      });
      const itemMap = new Map();
      parsedJSON?.offlineDownloadedItemList?.forEach((item) => {
        // if (item?.state == !CONTENT_DOWNLOAD_STATUS.DOWNLOAD_ALREADY_DOWNLOADED) hasProgress = true;
        itemMap.set(item?.url, item);
      });
      updateOfflineDownloadedItemList(itemMap);
      // if (!hasProgress) handleClearCheckProgressInterval();
    };

    // temporarily adding onFileDownloadProgress to handle bug for android
    // window.onFileDownloadProgress = (progressObj) => {
    //   const parsedJSON = safeJsonParse(progressObj, {
    //     source: "ContentList [onFileDownloadProgress]",
    //     fallback: {},
    //   });
    //   const item = parsedJSON?.progress;
    //   updateOfflineDownloadedItemList([[item?.url, item]]);
    // };
  };

  // FUNCTION TO UPDATE OFFLINE DOWNLOADED ITEM LIST
  const updateOfflineDownloadedItemList = (itemMap) => {
    const updatedMap = new Map([...itemMap]);
    offlineDownloadedListMapRef.current = updatedMap;
    87;
    setOfflineDownloadedListMap(offlineDownloadedListMapRef.current);
  };

  const showNotesModal = useSelector(
    (state) => state?.courseContentReducer?.showNotesModal
  );

  const notesContent = useSelector(
    (state) => state?.courseContentReducer?.notesContent
  );

  return (
    <React.Fragment>
      {featuredVideo?.name && (
        <FeaturedVideo
          videoItem={featuredVideo}
          getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
          getOfflineDownloadedItemState={getOfflineDownloadedItemState}
          handleGetOfflineDownloadList={handleGetOfflineDownloadList}
          handleSetCheckProgressInterval={handleSetCheckProgressInterval}
          handleClearCheckProgressInterval={handleClearCheckProgressInterval}
        />
      )}
      <div className={styles.contentList}>
        {!contentList?.length && !contentLoading ? (
          noContentText ? (
            <NoContentScreen
              noContentText={noContentText}
              subtext={subtext}
              height={noContentHeight ? noContentHeight : null}
            />
          ) : null
        ) : null}

        {contentHeading && contentList?.length ? (
          <h1>{contentHeading}</h1>
        ) : null}

        {contentList?.map?.((content, index) => (
          <React.Fragment key={content.id || index}>
            {(content?.hasAttachments || content?.attachments?.length) ? (<>
              <div>
                <ListItemForAttachments
                  key={content.id || index}
                  content={content}
                  {...(index === contentList?.length - 4 && handleLoadMoreContent
                    ? { ref: lastRowRef }
                    : {})}
                />
              </div>
            </>): (
              <ListItem
                key = {content.id || index}
                content={content}
                getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
                getOfflineDownloadedItemState={getOfflineDownloadedItemState}
                handleGetOfflineDownloadList={handleGetOfflineDownloadList}
                handleSetCheckProgressInterval={handleSetCheckProgressInterval}
                handleClearCheckProgressInterval={handleClearCheckProgressInterval}
                {...(index === contentList?.length - 4 && handleLoadMoreContent
                  ? { ref: lastRowRef }
                  : {})}
                isTrialContent={isTrialContent}
                />
            )}
            
          </React.Fragment>
        ))}
        
        {(contentLoading || router.isFallback) && (
          <ContentShimmmer
            withHeader={
              router.isFallback || (!contentListLength && contentLoading)
            }
          />
        )}
        {showNotesModal && (
          <NotesAttachmentModal 
            content={notesContent}
            getOfflineDownloadedItemProgress={getOfflineDownloadedItemProgress}
            getOfflineDownloadedItemState={getOfflineDownloadedItemState}
            handleGetOfflineDownloadList={handleGetOfflineDownloadList}
            handleSetCheckProgressInterval={handleSetCheckProgressInterval}
            handleClearCheckProgressInterval={handleClearCheckProgressInterval}
          />)}
      </div>
    </React.Fragment>
  );
};

export default ContentList;
